import { PbUser } from '@itsrever/rever-api'

export interface StoreGroup {
  // What the navbar selects and persists: language-independent and unique per group.
  key: string
  name: string
  // The group id backends know; undefined for legacy stores without a group and for REVER staff.
  id?: string
  ecommerces: string[]
}

export interface VisibleStoreGroup {
  group: StoreGroup
  stores: string[]
  hidden: number
}

export const COLLAPSED_STORES = 2
const ALL_GROUPS = '*'
const NO_GROUP_KEY_PREFIX = 'nogroup:'

export const toGroupName = (group: string | undefined, allLabel: string) =>
  group && group !== ALL_GROUPS ? group : allLabel

export const seesEveryGroup = (user: PbUser | undefined) =>
  user?.group === ALL_GROUPS

export const toStoreGroups = (
  user: PbUser | undefined,
  allLabel: string,
  noGroupLabel: string
): StoreGroup[] => {
  if (user?.groups?.length) {
    return user.groups.map((group) => {
      const ecommerces = group.e_commerces_ids ?? []
      if (group.id) {
        return { key: group.id, name: group.id, id: group.id, ecommerces }
      }
      return {
        key: NO_GROUP_KEY_PREFIX + (ecommerces[0] ?? ''),
        name: unnamedGroupName(ecommerces, noGroupLabel),
        ecommerces
      }
    })
  }
  // A settings-api without groups on Me puts every store in the user's own group.
  const ecommerces = user?.e_commerces_ids ?? []
  if (!ecommerces.length) return []
  const id = user?.group && !seesEveryGroup(user) ? user.group : undefined
  return [
    {
      key: user?.group || ALL_GROUPS,
      name: toGroupName(user?.group, allLabel),
      id,
      ecommerces
    }
  ]
}

export interface Selection {
  ecommerce?: string
  group?: string
}

// Drops a stored store or group the user lost, and picks a user's only store for per-store pages.
export const toValidSelection = (
  user: PbUser | undefined,
  selection: Selection
): Selection => {
  const stores = user?.e_commerces_ids ?? []
  if (selection.ecommerce && stores.includes(selection.ecommerce))
    return { ecommerce: selection.ecommerce }
  if (stores.length === 1) return { ecommerce: stores[0] }
  const groups = toStoreGroups(user, '', '')
  if (findGroupByKey(groups, selection.group)) return { group: selection.group }
  return {}
}

export const findGroupByKey = (
  groups: StoreGroup[],
  key: string | undefined
) => (key ? groups.find((group) => group.key === key) : undefined)

export const findStoreGroup = (groups: StoreGroup[], ecommerceId: string) =>
  groups.find((group) => group.ecommerces.includes(ecommerceId))

export const groupOfStore = (user: PbUser | undefined, ecommerceId: string) => {
  const group = user?.groups?.find((g) =>
    g.e_commerces_ids?.includes(ecommerceId)
  )
  if (group) return group.id || undefined
  if (user?.group && user.group !== ALL_GROUPS) return user.group
  return undefined
}

// A group matching the search keeps all its stores; otherwise only matching stores show.
export const filterStoreGroups = (
  groups: StoreGroup[],
  query: string,
  visibleCounts: Record<string, number>
): VisibleStoreGroup[] => {
  const term = query.trim().toLowerCase()
  return groups.flatMap((group) => {
    const stores = isStoreItself(group) ? [] : group.ecommerces
    if (!term || group.name.toLowerCase().includes(term)) {
      const count = Object.hasOwn(visibleCounts, group.key)
        ? visibleCounts[group.key]
        : COLLAPSED_STORES
      return [
        {
          group,
          stores: stores.slice(0, count),
          hidden: Math.max(stores.length - count, 0)
        }
      ]
    }
    const matching = stores.filter((ecom) => ecom.toLowerCase().includes(term))
    return matching.length ? [{ group, stores: matching, hidden: 0 }] : []
  })
}

// Some legacy stores have an empty group; a lone one shows as itself.
const unnamedGroupName = (ecommerces: string[], noGroupLabel: string) =>
  ecommerces.length === 1 ? ecommerces[0] : noGroupLabel

const isStoreItself = (group: StoreGroup) =>
  group.ecommerces.length === 1 && group.ecommerces[0] === group.name
